import { useEffect, useState } from "react";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileBarChart, Sparkles, Boxes, ListChecks, Workflow, Zap, ShieldCheck, BarChart3,
  Plug, Database, ClipboardList, HelpCircle, AlertTriangle, Layers, Info, Download, ChevronDown, Archive, FileSpreadsheet,
} from "lucide-react";

const SECTIONS = [
  { key: "business_overview", title: "Business Overview", icon: Info, type: "text" },
  { key: "recommended_process", title: "Recommended Salesforce Process", icon: Workflow, type: "text" },
  { key: "objects", title: "Objects", icon: Boxes, type: "chips" },
  { key: "fields", title: "Fields by Object", icon: Layers, type: "map" },
  { key: "processes", title: "Processes", icon: Workflow, type: "map" },
  { key: "automation", title: "Automation Requirements", icon: Zap, type: "list" },
  { key: "validation", title: "Validation Requirements", icon: ListChecks, type: "list" },
  { key: "approvals", title: "Approval Requirements", icon: ShieldCheck, type: "list" },
  { key: "reports", title: "Reports & Dashboards", icon: BarChart3, type: "list" },
  { key: "integrations", title: "Integrations", icon: Plug, type: "list" },
  { key: "data_migration", title: "Data Migration", icon: Database, type: "list" },
  { key: "security", title: "Security & Access", icon: ShieldCheck, type: "list" },
  { key: "open_questions", title: "Open Questions", icon: HelpCircle, type: "list" },
  { key: "assumptions", title: "Assumptions", icon: ClipboardList, type: "list" },
  { key: "out_of_scope", title: "Out of Scope / Future", icon: AlertTriangle, type: "list" },
];

export default function Blueprint() {
  const { project } = useProject();
  const { user } = useAuth();
  const [bp, setBp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const canGenerate = user?.role === "admin" || user?.role === "consultant";

  useEffect(() => { api.get(`/projects/${project.id}/blueprint`).then((r) => setBp(r.data)).finally(() => setLoading(false)); }, [project.id]);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/blueprint/generate`);
      setBp(data);
      toast.success("Blueprint generated");
    } catch (e) { toast.error(apiErr(e, "Generation failed")); }
    finally { setGen(false); }
  };

  const content = bp?.content;

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/projects/${project.id}/blueprint/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "implementation-blueprint.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/projects/${project.id}/blueprint/export.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "implementation-blueprint.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="relative brand-gradient p-8 text-white">
            <div className="absolute inset-0 subtle-grid opacity-20" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wide backdrop-blur"><FileBarChart className="h-3.5 w-3.5" /> Implementation Blueprint</span>
                <h1 className="mt-3 font-heading text-2xl font-black tracking-tight">{project.project_name}</h1>
                <p className="mt-1 text-sm text-sky-100">Generated from captured discovery — to be reviewed and validated by a consultant.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {content && (
                  <Button data-testid="export-blueprint-pdf" onClick={downloadPdf} variant="outline" className="rounded-lg border-white/40 bg-white/10 text-white hover:bg-white/20">
                    <Download className="mr-1.5 h-4 w-4" /> PDF
                  </Button>
                )}
                {content && (
                  <Button data-testid="export-blueprint-xlsx" onClick={downloadXlsx} variant="outline" className="rounded-lg border-white/40 bg-white/10 text-white hover:bg-white/20">
                    <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
                  </Button>
                )}
                {canGenerate && (
                  <Button data-testid="generate-blueprint" onClick={generate} disabled={gen} className="rounded-lg bg-white text-sky-700 hover:bg-sky-50">
                    <Sparkles className="mr-1.5 h-4 w-4" /> {gen ? "Generating…" : content ? "Regenerate" : "Generate Blueprint"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 border-b border-slate-100 px-8 py-4 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" /> Customer Requirement</span>
            <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Suggested Salesforce Approach</span>
            <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Consultant Validated</span>
          </div>
        </div>

        {loading && <div className="mt-8 text-center text-slate-400">Loading…</div>}

        {!loading && !content && (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50"><Sparkles className="h-7 w-7 text-sky-500" /></div>
            <p className="font-heading text-lg font-bold text-slate-700">No blueprint yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">Generate a structured implementation blueprint from the discovery answers captured so far. A consultant should review and validate before use.</p>
            {canGenerate && <Button onClick={generate} disabled={gen} className="mt-5 rounded-lg bg-sky-600 hover:bg-sky-700"><Sparkles className="mr-1.5 h-4 w-4" /> {gen ? "Generating…" : "Generate Blueprint"}</Button>}
          </div>
        )}

        {content && (
          <>
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This blueprint is generated from captured requirements as a starting point. It is <strong>&nbsp;not&nbsp;</strong> an automatically architected solution — a Salesforce consultant must review and validate it.
            </div>
            <div className="mt-6 space-y-4">
              {SECTIONS.map((s) => <BlueprintSection key={s.key} section={s} value={content[s.key]} />)}
            </div>
            {bp.captured?.length > 0 && <CapturedDetail captured={bp.captured} />}
            <p className="mt-6 text-center text-xs text-slate-400">Generated {bp.generated_at ? new Date(bp.generated_at).toLocaleString() : ""} {bp.generated_by ? `· by ${bp.generated_by}` : ""}</p>
          </>
        )}
      </div>
    </div>
  );
}

function CapturedDetail({ captured }) {
  const [open, setOpen] = useState(true);
  const total = captured.reduce((n, s) => n + (s.items?.length || 0), 0);
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="captured-detail">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 p-6 text-left hover:bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100"><Archive className="h-4.5 w-4.5 text-slate-600" /></div>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Captured Discovery Detail</h3>
            <p className="text-xs text-slate-500">Complete source record — every one of the {total} captured answers. Nothing summarised away.</p>
          </div>
        </div>
        <ChevronDown className={"h-4 w-4 text-slate-400 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-slate-100 p-6">
          {captured.map((sec, i) => (
            <div key={i} data-testid={`captured-module-${i}`}>
              <div className="flex items-center gap-2">
                <div className="font-heading text-sm font-bold text-sky-700">{sec.module}</div>
                {sec.out_of_scope && <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-600 ring-1 ring-amber-200">Out of scope</span>}
              </div>
              <div className="mt-2 space-y-2">
                {sec.items?.map((it, j) => (
                  <div key={j} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="text-xs font-semibold text-slate-500">{it.question}</div>
                    <div className="mt-0.5 text-sm text-slate-800">{it.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderItem(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.map(renderItem).join(", ");
  if (typeof v === "object") return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(" · ");
  return String(v);
}

function BlueprintSection({ section, value }) {
  const empty = value == null || (Array.isArray(value) && value.length === 0) || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50"><section.icon className="h-4.5 w-4.5 text-sky-600" /></div>
        <h3 className="font-heading text-base font-bold text-slate-900">{section.title}</h3>
      </div>
      <div className="mt-4 text-sm text-slate-600">
        {empty ? <span className="italic text-slate-400">Not enough information captured yet.</span> : (
          <>
            {section.type === "text" && <p className="leading-relaxed">{renderItem(value)}</p>}
            {section.type === "chips" && <div className="flex flex-wrap gap-2">{value.map((v, i) => <span key={i} className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700">{renderItem(v)}</span>)}</div>}
            {section.type === "list" && <ul className="space-y-1.5">{value.map((v, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" /> {renderItem(v)}</li>)}</ul>}
            {section.type === "map" && (
              <div className="space-y-3">
                {Object.entries(value).map(([k, v]) => (
                  <div key={k}>
                    <div className="font-heading text-sm font-bold text-slate-700">{k}</div>
                    <div className="mt-1 text-slate-600">{Array.isArray(v) ? <div className="flex flex-wrap gap-1.5">{v.map((x, i) => <span key={i} className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">{renderItem(x)}</span>)}</div> : <p>{renderItem(v)}</p>}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
